/**
 * Visit attribution for lead forms.
 *
 * First touch = how the visitor arrived (landing page, external referrer,
 * utm_*, ad click ids). It's captured once per page load into module memory,
 * which survives SPA navigation without writing anything to the device. It is
 * persisted to localStorage (30 days) only when the visitor has granted
 * analytics consent, so a return visit keeps its original source.
 *
 * Last touch = the page the form is submitted from.
 */
import { readConsent } from "@/utils/consentCookie";
import { sanitiseExternalReferrer, sanitiseSitePath } from "@/utils/measurementPolicy";

import campaignValues from "@/data/measurementCampaigns.json";
// Version 1 stored arbitrary URL/query and click-ID values. Do not migrate it:
// a locally injected legacy record must not become CRM attribution.
const LEGACY_STORAGE_KEY = "cdp_first_touch_v1";
const STORAGE_KEY = "cdp_first_touch_v2";
const STORAGE_VERSION = 2;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const PARAM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "gclid", "fbclid", "msclkid",
];

const SITE_HOSTS = new Set(["candopayments.com", "www.candopayments.com", "d2hqz144sz4ut1.cloudfront.net"]);

function allowedValue(key, value) {
  if (typeof value !== "string" || !value) return "";
  // The generated projection is the only source accepted by both form
  // attribution and page-view campaign transport. Click IDs are never kept.
  return (campaignValues[key] || []).includes(value) ? value : "";
}

let firstTouch = null;

function readParams(search) {
  const params = new URLSearchParams(search || "");
  const out = {};
  PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (key.startsWith("utm_")) {
      const allowed = allowedValue(key, value);
      if (allowed) out[key] = allowed;
    }
  });
  return out;
}

function hasAnalyticsConsent() {
  try {
    return Boolean(readConsent()?.analytics);
  } catch {
    return false;
  }
}

function pageUrlForPath(path, location) {
  if (!path || !location) return "";
  try {
    const origin = location.origin || new URL(location.href).origin;
    return `${origin}${path}`;
  } catch {
    return "";
  }
}

function locationForAttribution(location, firstInput, lastInput) {
  const candidates = [firstInput.landing_page, lastInput.page_url];
  for (const value of candidates) {
    try {
      const url = new URL(value);
      if (url.protocol === "https:" && SITE_HOSTS.has(url.hostname.toLowerCase())) {
        return { href: url.href, origin: url.origin, hostname: url.hostname };
      }
    } catch { /* only absolute approved site URLs can establish a context */ }
  }
  return location;
}

function validCapturedAt(value, now) {
  if (typeof value !== "string") return "";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp) || timestamp > now || now - timestamp >= TTL_MS) return "";
  return new Date(timestamp).toISOString();
}

/**
 * Construct the only attribution shape that may be stored or sent. Values
 * arriving through storage or a direct submission are treated as untrusted.
 */
export function sanitiseAttribution(input, { location = typeof window !== "undefined" ? window.location : null, now = Date.now() } = {}) {
  const candidate = input && typeof input === "object" ? input : {};
  const firstInput = candidate.first_touch && typeof candidate.first_touch === "object" ? candidate.first_touch : {};
  const lastInput = candidate.last_touch && typeof candidate.last_touch === "object" ? candidate.last_touch : {};
  const safeLocation = locationForAttribution(location, firstInput, lastInput);
  const capturedAt = validCapturedAt(firstInput.captured_at, now);
  const firstPath = typeof firstInput.landing_page === "string" && firstInput.landing_page
    ? sanitiseSitePath(firstInput.landing_page, safeLocation) : "";
  const lastPath = typeof lastInput.page_url === "string" && lastInput.page_url
    ? sanitiseSitePath(lastInput.page_url, safeLocation) : "";
  const landingPage = pageUrlForPath(firstPath, safeLocation);
  const referrer = sanitiseExternalReferrer(firstInput.referrer || "", safeLocation);
  const first = (capturedAt || landingPage || referrer) ? {
    landing_page: landingPage,
    referrer,
    ...(capturedAt ? { captured_at: capturedAt } : {}),
  } : {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((key) => {
    const value = allowedValue(key, firstInput[key]);
    if (value) first[key] = value;
  });
  return {
    first_touch: first,
    last_touch: { page_url: pageUrlForPath(lastPath, safeLocation) },
  };
}

function loadStored(now) {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    if (stored?.version === STORAGE_VERSION && stored.attribution) {
      const safe = sanitiseAttribution(stored.attribution, { now }).first_touch;
      if (safe.captured_at) return safe;
    }
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* blocked storage or malformed value — treat as absent */
  }
  return null;
}

function persistIfConsented() {
  if (!firstTouch || !hasAnalyticsConsent()) return;
  try {
    const safe = sanitiseAttribution({ first_touch: firstTouch }).first_touch;
    if (!safe.captured_at) return;
    firstTouch = safe;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, attribution: { first_touch: safe } }));
  } catch {
    /* storage blocked — memory copy still works for this visit */
  }
}

function clearStored() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch { /* storage unavailable */ }
}

function clearLegacyStored() {
  try { window.localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* storage unavailable */ }
}

function captureCurrent(location, referrer, now, { includeAcquisition = true } = {}) {
  const path = sanitiseSitePath(location.href, location);
  const origin = location.origin || new URL(location.href).origin;
  return sanitiseAttribution({ first_touch: {
    landing_page: path ? `${origin}${path}` : "",
    referrer: includeAcquisition ? sanitiseExternalReferrer(referrer, location) : "",
    captured_at: new Date(now).toISOString(),
    ...(includeAcquisition ? readParams(location.search) : {}),
  } }, { location, now }).first_touch;
}

/** Call once at app start (index.js). Safe to call again; later calls are no-ops. */
export function initAttribution({ location = window.location, referrer = document.referrer, now = Date.now() } = {}) {
  if (firstTouch) return firstTouch;
  // Do not keep a pre-choice landing URL, referrer, campaign query or click
  // identifier in memory for later replay into analytics or CRM attribution.
  if (!hasAnalyticsConsent()) return null;
  // Do not reuse v1. Its unbounded values cannot be safely attributed after
  // this release; clear it only once storage is eligible for analytics use.
  clearLegacyStored();
  const stored = loadStored(now);
  firstTouch = stored || captureCurrent(location, referrer, now);
  persistIfConsented();
  return firstTouch;
}

/** Called by the consent lifecycle after a new grant, never on a replay. */
export function startAttributionAfterConsent({ location = window.location, now = Date.now() } = {}) {
  if (firstTouch) return firstTouch;
  // The visitor may have navigated while refusing analytics. Starting from the
  // current route without referrer/query context prevents that history being
  // retained when consent changes later in the session.
  clearLegacyStored();
  firstTouch = loadStored(now) || captureCurrent(location, "", now, { includeAcquisition: false });
  persistIfConsented();
  return firstTouch;
}

/** Withdrawal clears analytics-only acquisition state immediately. */
export function clearAttributionForConsentWithdrawal() {
  firstTouch = null;
  clearStored();
}

/** { first_touch, last_touch } for the Note, taken at submit time. */
export function getAttribution({ location = window.location } = {}) {
  const first = firstTouch || initAttribution({ location }) || {};
  persistIfConsented(); // consent may have been granted since landing
  const attribution = {
    first_touch: first,
    last_touch: hasAnalyticsConsent()
      ? { page_url: pageUrlForPath(sanitiseSitePath(location.href, location), location) }
      : { page_url: "" },
  };
  return sanitiseAttribution(attribution, { location });
}

/** CRM-keyed attribution fields. First-touch UTMs only; internal navigation cannot fill missing acquisition fields. */
export function attributionToCrm({ first_touch: first = {}, last_touch: last = {} }) {
  const safe = sanitiseAttribution({ first_touch: first, last_touch: last });
  const pick = (key) => safe.first_touch[key] || "";
  return {
    UTM_Source: pick("utm_source"),
    UTM_Medium: pick("utm_medium"),
    UTM_Campaign: pick("utm_campaign"),
    UTM_Term: pick("utm_term"),
    UTM_Content: pick("utm_content"),
    // Retain blank-compatible CRM keys but do not collect click IDs pending a
    // separately governed attribution design.
    Google_Click_ID: "",
    FBCLID: "",
    Landing_Page: safe.first_touch.landing_page,
    Referring_URL: safe.first_touch.referrer,
    Submitted_From_URL: safe.last_touch.page_url,
  };
}

/** Test hook. */
export function _resetAttributionForTests() {
  firstTouch = null;
}
